import React, { useState, useEffect, useMemo } from 'react';

const STATUS_BY_MODE = {
  standard: [
    { text: "Reading your PDF sides (structured extraction)...", emoji: "📖" },
    { text: "Building full character + scene analysis...", emoji: "🎭" },
    { text: "Compressing analysis into a focused 300-word brief...", emoji: "🧠" },
    { text: "Generating your final HTML Prep101 guide...", emoji: "✨" },
    { text: "Writing Two-Take Strategy (Take A + Take B)...", emoji: "🎯" },
    { text: "Completing Pre-Submission Checklist...", emoji: "📋" },
    { text: "Finalizing Coach Note and end-of-scene instruction...", emoji: "🎬" },
    { text: "Running completion checks for truncation safety...", emoji: "🛡️" },
    { text: "Polishing your final guide for delivery...", emoji: "✍️" },
    { text: "Almost there - packaging your guide now...", emoji: "🎪" },
  ],
  reader_support: [
    { text: "Reading your uploaded sides...", emoji: "📄" },
    { text: "Mapping pacing and line flow...", emoji: "⏱️" },
    { text: "Identifying support beats for the reader...", emoji: "🎧" },
    { text: "Building cue-by-cue reader notes...", emoji: "📝" },
    { text: "Flagging tone shifts and pauses...", emoji: "🎬" },
    { text: "Preparing practical self-tape support tips...", emoji: "🎙️" },
    { text: "Finalizing your Reader101 guide...", emoji: "✅" },
  ],
};

const LoadingSpinner = ({ mode = 'standard' }) => {
  const statusMessages = useMemo(
    () => STATUS_BY_MODE[mode] || STATUS_BY_MODE.standard,
    [mode]
  );
  const [messageIndex, setMessageIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    // Rotate status messages every 25 seconds
    const messageInterval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % statusMessages.length);
    }, 25000);

    // Update elapsed time every second
    const timeInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(timeInterval);
    };
  }, [statusMessages.length]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const currentStatus = statusMessages[messageIndex];

  return (
    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
      <div style={{ display: 'inline-block', maxWidth: '400px' }}>
        {/* Animated spinner */}
        <div style={{
          width: '80px',
          height: '80px',
          border: '5px solid rgba(251, 191, 36, 0.2)',
          borderTop: '5px solid #fbbf24',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1.5rem'
        }}></div>
        {/* Main status message */}
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#fbbf24',
          marginBottom: '0.75rem',
          minHeight: '2rem',
          transition: 'opacity 0.3s ease'
        }}>
          {currentStatus.emoji} {currentStatus.text}
        </h3>

        {/* Elapsed time */}
        <p style={{
          color: '#9ca3af',
          fontSize: '0.875rem',
          marginBottom: '1rem'
        }}>
          Time elapsed: {formatTime(elapsedTime)}
        </p>

        {/* Progress indicator dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '1rem'
        }}>
          {statusMessages.slice(0, 5).map((_, index) => (
            <div
              key={index}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: index <= messageIndex ? '#fbbf24' : 'rgba(251, 191, 36, 0.2)',
                transition: 'background 0.3s ease'
              }}
            />
          ))}
        </div>

        {/* Helpful tip */}
        <div style={{
          background: 'rgba(251, 191, 36, 0.1)',
          borderRadius: '12px',
          padding: '1rem',
          marginTop: '1rem'
        }}>
          <p style={{
            color: '#e5e7eb',
            fontSize: '0.875rem',
            margin: 0
          }}>
            <strong style={{ color: '#fbbf24' }}>Pro tip:</strong> Prep101 now runs extraction, analysis, summary, and final guide assembly before delivery.
            Complex scripts or multiple files may take longer.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoadingSpinner;
